import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useCategories } from '../contexts/CategoryContext';
import axios from 'axios';
import api from '../api/axios';
import logo from '../assets/images/hideaway-logo.svg';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

const FeaturedProductsCarousel = ({ products, onProductClick }) => {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(products.length > 3);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (products.length <= 3) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 h-full">
            {products.map(product => {
                const imageUrl = product.image || (product.images && product.images[0]);
                return (
                    <div 
                        key={product.id}
                        className="group/product cursor-pointer flex flex-col h-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          onProductClick(product.id);
                        }}
                    >
                        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-md bg-white mb-3 shadow-sm">
                            {imageUrl ? (
                                <img 
                                src={imageUrl} 
                                alt={product.name}
                                className="object-contain w-full h-full group-hover/product:scale-105 transition-transform duration-500"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">No Image</div>
                            )}
                        </div>
                        
                        <h3 className="text-sm font-bold text-gray-900 mb-1 uppercase tracking-wide group-hover/product:text-primary transition-colors line-clamp-1">
                            {product.name}
                        </h3>
                        
                        <p className="text-xs text-gray-500 mb-3 line-clamp-2">
                            {product.description}
                        </p>
                        
                        <div className="mt-auto">
                            <span className="text-xs font-bold uppercase tracking-wider text-black border-b border-black hover:text-primary hover:border-primary transition-colors pb-0.5">
                                Shop Now
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
      );
  }

  return (
      <div className="relative h-full group/carousel px-8">
          {/* Arrows */}
          {showLeftArrow && (
              <button 
                onClick={(e) => { e.stopPropagation(); scroll('left'); }}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 text-gray-800 p-2 rounded-full shadow-md transition-all border border-gray-100"
              >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
              </button>
          )}
          
          {showRightArrow && (
              <button 
                onClick={(e) => { e.stopPropagation(); scroll('right'); }}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 text-gray-800 p-2 rounded-full shadow-md transition-all border border-gray-100"
              >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
              </button>
          )}

          {/* Scroll Container */}
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex gap-6 overflow-x-auto h-full scrollbar-hide scroll-smooth pb-2 items-stretch"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
              {products.map(product => {
                   const imageUrl = product.image || (product.images && product.images[0]);
                   return (
                       <div key={product.id} className="min-w-[280px] w-[280px] h-full flex-shrink-0">
                            <div 
                                className="group/product cursor-pointer flex flex-col h-full"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onProductClick(product.id);
                                }}
                            >
                                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-md bg-white mb-3 shadow-sm">
                                    {imageUrl ? (
                                        <img 
                                        src={imageUrl} 
                                        alt={product.name}
                                        className="object-contain w-full h-full group-hover/product:scale-105 transition-transform duration-500"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400">No Image</div>
                                    )}
                                </div>
                                
                                <h3 className="text-sm font-bold text-gray-900 mb-1 uppercase tracking-wide group-hover/product:text-primary transition-colors line-clamp-1">
                                    {product.name}
                                </h3>
                                
                                <p className="text-xs text-gray-500 mb-3 line-clamp-2">
                                    {product.description}
                                </p>
                                
                                <div className="mt-auto">
                                    <span className="text-xs font-bold uppercase tracking-wider text-black border-b border-black hover:text-primary hover:border-primary transition-colors pb-0.5">
                                        Shop Now
                                    </span>
                                </div>
                            </div>
                       </div>
                   );
              })}
          </div>
      </div>
  )
}

const Navbar = () => {
  const { currentUser, logout, loading: authLoading } = useAuth();
  const { getCartItemCount, openMiniCart } = useCart();
  const { categories: allCategories, loading: categoriesLoading } = useCategories();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navCategories, setNavCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  // Mega menus stay in the DOM (hidden) for their open/close transition. Only
  // render their images once a menu has been opened, otherwise every page load
  // downloads every category's full-size image.
  const [openedMenus, setOpenedMenus] = useState([]);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [expandedMobileCategory, setExpandedMobileCategory] = useState(null);
  const desktopSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);

  const submitNavbarSearch = (inputEl, closeMobileMenu) => {
    const el = inputEl || desktopSearchRef.current || mobileSearchRef.current;
    const q = el?.value?.trim();
    if (!q) return;
    navigate(`/products?search=${encodeURIComponent(q)}`);
    if (closeMobileMenu) setMenuOpen(false);
  };

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser) {
        try {
          const response = await api.get('/auth/me');
          setIsAdmin(response.data.claims.role === 'admin');
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [currentUser]);

  useEffect(() => {
    if (categoriesLoading || allCategories.length === 0) return;

    const buildNavCategories = async () => {
      try {
        const cachedNav = sessionStorage.getItem('navbar_config');
        let config;
        if (cachedNav) {
          config = JSON.parse(cachedNav);
        } else {
          const navbarConfigRes = await axios.get(`${API_BASE_URL}/admin/navbar/config`);
          config = navbarConfigRes.data;
          try { sessionStorage.setItem('navbar_config', JSON.stringify(config)); } catch {}
        }

        let finalCategories;

        if (config.categories && config.categories.length > 0) {
          finalCategories = config.categories
            .filter(nc => nc.visible !== false)
            .map(nc => {
              const subcategories = allCategories.filter(c => c.parent_id === nc.category_id);
              return { 
                id: nc.category_id,
                name: nc.name,
                slug: nc.slug,
                description: nc.description,
                image: nc.image,
                navOrder: nc.order,
                subcategories: subcategories,
                featured_products: nc.featured_products || []
              };
            })
            .filter(Boolean)
            .sort((a, b) => a.navOrder - b.navOrder);
        } else {
          const parentCategories = allCategories.filter(c => !c.parent_id);
          finalCategories = parentCategories.map(cat => ({
            ...cat,
            subcategories: allCategories.filter(c => c.parent_id === cat.id),
            featured_products: []
          }));
        }

        setNavCategories(finalCategories);
        setLoading(false);
      } catch (error) {
        console.error('Failed to build nav categories:', error);
        setLoading(false);
      }
    };

    buildNavCategories();
  }, [allCategories, categoriesLoading]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/products?category=${categoryId}`);
    setMenuOpen(false);
    setActiveCategory(null);
  };

  const cartItemCount = getCartItemCount();

  return (
    <nav className="sticky top-0 z-50 w-full font-sans">
      {/* Top Bar */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[72px]">
            
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center group">
              <img src={logo} alt="HideawayHome" className="h-5 sm:h-6 md:h-9 lg:h-10 w-auto transition-transform duration-300 group-hover:scale-[1.03]" />
            </Link>

            {/* Search Bar - Desktop */}
            <div className="hidden md:block flex-1 max-w-xl mx-10">
              <div className="relative group/search">
                <input
                  ref={desktopSearchRef}
                  type="search"
                  placeholder="What are you looking for?"
                  className="w-full bg-gray-50 text-gray-900 rounded-full py-2.5 pl-5 pr-12 border border-gray-200 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 focus:bg-white transition-all duration-200 text-sm placeholder:text-gray-400"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submitNavbarSearch(e.currentTarget, false);
                    }
                  }}
                />
                <button
                  type="button"
                  aria-label="Search"
                  className="absolute right-1 top-1/2 -translate-y-1/2 z-10 bg-primary hover:bg-primary-dark text-white rounded-full p-2 transition-colors duration-200"
                  onClick={() => submitNavbarSearch(desktopSearchRef.current, false)}
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden>
                    <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              {currentUser ? (
                <>
                  <button type="button" onClick={openMiniCart} aria-label="Open cart" className="relative p-2.5 hover:bg-gray-50 rounded-full transition-all duration-200 group hidden md:flex items-center gap-2 text-gray-600 hover:text-primary">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="transition-transform duration-200 group-hover:scale-110">
                      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 6h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 10a4 4 0 01-8 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {cartItemCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-accent text-white text-[10px] font-bold rounded-full h-[18px] min-w-[18px] flex items-center justify-center px-1 shadow-sm">
                        {cartItemCount}
                      </span>
                    )}
                    <span className="text-xs font-medium hidden sm:block">Cart</span>
                  </button>
                  
                  <div className="hidden md:block w-px h-6 bg-gray-200 mx-1"></div>

                  <div 
                    className="relative hidden md:block"
                    onMouseEnter={() => setAccountMenuOpen(true)}
                    onMouseLeave={() => setAccountMenuOpen(false)}
                  >
                    <button className="p-2.5 hover:bg-gray-50 rounded-full transition-all duration-200 group flex items-center gap-2 text-gray-600 hover:text-primary">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="transition-transform duration-200 group-hover:scale-110">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className="text-xs font-medium hidden sm:block">Account</span>
                    </button>
                    
                    {/* Account Dropdown */}
                    <div className={`absolute right-0 top-full pt-2 w-48 z-50 transform transition-all duration-200 origin-top-right ${accountMenuOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                      <div className="bg-white rounded-md shadow-lg py-1 text-gray-800 ring-1 ring-black ring-opacity-5">
                      {/* Home */}
                      <Link 
                        to="/" 
                        className="block px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                         <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"></path>
                            <polyline points="9 22 9 12 15 12 15 22"></polyline>
                        </svg>
                        Home
                      </Link>

                      {/* Orders */}
                      <Link 
                        to="/orders" 
                        className="block px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                           <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                        Orders
                      </Link>

                      {/* Favorites */}
                      <Link 
                        to="/favorites" 
                        className="block px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Favorites
                      </Link>

                      <Link 
                        to="/profile" 
                        className="block px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Profile
                      </Link>
                      
                      {isAdmin && (
                        <Link 
                            to="/admin" 
                            className="block px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                            onClick={() => setAccountMenuOpen(false)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                            Admin
                        </Link>
                      )}

                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        onClick={() => {
                          setAccountMenuOpen(false);
                          handleLogout();
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Logout
                      </button>
                    </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="hidden md:flex items-center gap-2">
                  <button type="button" onClick={openMiniCart} aria-label="Open cart" className="relative p-2.5 hover:bg-gray-50 rounded-full transition-all duration-200 group flex items-center gap-2 text-gray-600 hover:text-primary">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="transition-transform duration-200 group-hover:scale-110">
                      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 6h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 10a4 4 0 01-8 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {cartItemCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-accent text-white text-[10px] font-bold rounded-full h-[18px] min-w-[18px] flex items-center justify-center px-1 shadow-sm">
                        {cartItemCount}
                      </span>
                    )}
                    <span className="text-xs font-medium hidden sm:block">Cart</span>
                  </button>
                  <div className="w-px h-6 bg-gray-200 mx-1"></div>
                  {/* Keep their space but hide them until Firebase has checked for a
                      signed-in session, so returning customers don't see them flash. */}
                  <Link to="/login" className={`text-sm font-medium text-gray-600 hover:text-primary px-3 py-2 rounded-full hover:bg-gray-50 transition-all duration-200${authLoading ? ' invisible' : ''}`}>
                    Sign in
                  </Link>
                  <Link to="/signup" className={`bg-primary hover:bg-primary-dark text-white text-sm font-semibold py-2 px-5 rounded-full transition-all duration-200 hover:shadow-md${authLoading ? ' invisible' : ''}`}>
                    Sign Up
                  </Link>
                </div>
              )}

              {/* Mobile Cart Icon */}
              <button type="button" onClick={openMiniCart} aria-label="Open cart" className="relative md:hidden p-2 hover:bg-gray-50 rounded-full text-gray-600 transition-all duration-200">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 6h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 10a4 4 0 01-8 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {cartItemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-accent text-white text-[10px] font-bold rounded-full h-[18px] min-w-[18px] flex items-center justify-center px-1 shadow-sm">
                    {cartItemCount}
                  </span>
                )}
              </button>

              {/* Mobile Menu Button */}
              <button 
                className="md:hidden p-2 hover:bg-gray-50 rounded-full text-gray-600 transition-all duration-200"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Toggle menu"
              >
                <div className="w-6 h-6 relative flex items-center justify-center">
                  <span className={`absolute h-0.5 w-5 bg-current rounded-full transition-all duration-300 ${menuOpen ? 'rotate-45' : '-translate-y-1.5'}`}></span>
                  <span className={`absolute h-0.5 w-5 bg-current rounded-full transition-all duration-300 ${menuOpen ? 'opacity-0 scale-0' : 'opacity-100'}`}></span>
                  <span className={`absolute h-0.5 w-5 bg-current rounded-full transition-all duration-300 ${menuOpen ? '-rotate-45' : 'translate-y-1.5'}`}></span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Links - Bottom Bar (Desktop) */}
      <div className="hidden md:block bg-gradient-to-r from-primary-dark via-primary to-primary-dark text-white relative shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-11 text-[13px] font-medium">
            
            {/* Categories Loop */}
            <div className="flex items-center gap-0 h-full">
            {navCategories.map((category) => (
              <div 
                key={category.id}
                className="h-full flex items-center group/nav-item"
                onMouseEnter={() => {
                  setActiveCategory(category.id);
                  setOpenedMenus(ids => (ids.includes(category.id) ? ids : [...ids, category.id]));
                }}
                onMouseLeave={() => setActiveCategory(null)}
              >
                <button
                  onClick={() => handleCategoryClick(category.id)}
                  className={`relative flex items-center px-4 h-full uppercase tracking-[0.12em] transition-all duration-200 ${activeCategory === category.id ? 'text-white bg-white/10' : 'text-white/85 hover:text-white hover:bg-white/[0.06]'}`}
                >
                  {category.name}
                  <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] bg-accent rounded-full transition-all duration-300 ${activeCategory === category.id ? 'w-3/4' : 'w-0'}`}></span>
                </button>
                
                {/* Mega Menu - Full Width */}
                <div 
                  className={`absolute top-full left-0 w-full z-50 transition-all duration-300 ease-out ${activeCategory === category.id ? 'opacity-100 translate-y-0 visible' : 'opacity-0 -translate-y-2 invisible pointer-events-none'}`}
                >
                  <div className="h-[3px] bg-gradient-to-r from-accent via-accent/80 to-accent"></div>
                  <div className="bg-white text-gray-800 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.2)] border-b border-gray-100">
                    <div className="max-w-7xl mx-auto flex h-[420px]">
                      {/* Left Column: Subcategories & Links */}
                      <div className="min-w-[280px] flex-shrink-0 bg-gradient-to-b from-gray-50/80 to-white px-8 py-7 flex flex-col">
                        <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200/80">
                          {category.image && (
                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-white shadow-sm border border-gray-100 flex-shrink-0">
                              {openedMenus.includes(category.id) && (
                                <img src={category.image} alt="" className="w-full h-full object-cover" />
                              )}
                            </div>
                          )}
                          <div>
                            <h3 className="font-bold text-gray-900 uppercase tracking-wider text-xs">
                              {category.name}
                            </h3>
                            {category.description && (
                              <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-1">
                                {category.description}
                              </p>
                            )}
                          </div>
                        </div>

                        <ul className="grid grid-flow-col gap-y-0.5 gap-x-8 flex-1 content-start pr-1" style={{ gridTemplateRows: 'repeat(6, auto)' }}>
                          {category.subcategories && category.subcategories.map(subcategory => (
                            <li key={subcategory.id}>
                              <button
                                onClick={() => handleCategoryClick(subcategory.id)}
                                className="group/sub w-full text-left flex items-center gap-2 py-2 px-3 rounded-lg text-sm text-gray-600 hover:text-primary hover:bg-primary/[0.04] transition-all duration-150 whitespace-nowrap"
                              >
                                <span className="w-1 h-1 rounded-full bg-gray-300 group-hover/sub:bg-primary group-hover/sub:scale-150 transition-all duration-150 flex-shrink-0"></span>
                                <span>{subcategory.name}</span>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-0 -translate-x-1 group-hover/sub:opacity-60 group-hover/sub:translate-x-0 transition-all duration-150 flex-shrink-0">
                                  <path d="M9 18l6-6-6-6"/>
                                </svg>
                              </button>
                            </li>
                          ))}
                        </ul>

                        <button
                          onClick={() => handleCategoryClick(category.id)}
                          className="group/shopall mt-4 flex items-center gap-2 w-full py-2 text-sm font-semibold text-primary hover:text-primary-dark transition-all duration-200"
                        >
                          Shop All {category.name}
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-200 group-hover/shopall:translate-x-1">
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                          </svg>
                        </button>
                      </div>

                      {/* Divider */}
                      <div className="w-px bg-gradient-to-b from-gray-100 via-gray-200 to-gray-100"></div>

                      {/* Right Column: Featured Products or Category Image */}
                      <div className="flex-1 min-w-0 px-8 py-7">
                        {category.featured_products && category.featured_products.length > 0 ? (
                          <div className="h-full flex flex-col">
                            <div className="flex items-center justify-between mb-5">
                              <div className="flex items-center gap-2">
                                <div className="w-1 h-4 rounded-full bg-accent"></div>
                                <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400">Featured Products</h4>
                              </div>
                              <span className="text-[11px] text-gray-300 font-medium">{category.featured_products.length} items</span>
                            </div>
                            <div className="flex-1 min-h-0">
                              {openedMenus.includes(category.id) && (
                                <FeaturedProductsCarousel
                                  products={category.featured_products}
                                  onProductClick={(id) => {
                                      navigate(`/products/${id}`);
                                      setActiveCategory(null);
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        ) : category.image ? (
                          <div className="w-full h-full relative overflow-hidden rounded-xl group/cat-image cursor-pointer bg-gray-50" onClick={() => handleCategoryClick(category.id)}>
                            {openedMenus.includes(category.id) && (
                              <img
                                src={category.image}
                                alt={category.name}
                                className="object-cover w-full h-full group-hover/cat-image:scale-105 transition-transform duration-700 ease-out"
                              />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                            <div className="absolute bottom-0 left-0 right-0 p-8">
                              <h3 className="text-2xl font-bold uppercase tracking-wider mb-3 text-white drop-shadow-lg">{category.name} Collection</h3>
                              <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-white bg-white/20 backdrop-blur-sm hover:bg-white/30 px-5 py-2.5 rounded-full transition-colors duration-200">
                                Explore
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M5 12h14M12 5l7 7-7 7"/>
                                </svg>
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-gray-300 bg-gray-50/50 rounded-xl border-2 border-dashed border-gray-100">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="mb-3 text-gray-200">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                            </svg>
                            <span className="text-sm font-medium">Browse {category.name}</span>
                            <span className="text-xs text-gray-300 mt-1">Click a subcategory to explore</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
             <Link 
               to="/products" 
               className={`relative flex items-center px-4 h-full uppercase tracking-[0.12em] transition-all duration-200 ${location.pathname === '/products' ? 'text-white bg-white/10' : 'text-white/85 hover:text-white hover:bg-white/[0.06]'}`}
             >
               Shop All
               <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] bg-accent rounded-full transition-all duration-300 ${location.pathname === '/products' ? 'w-3/4' : 'w-0'}`}></span>
             </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden bg-white shadow-lg transition-all duration-300 ease-in-out ${menuOpen ? 'max-h-[85vh] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        <div className="px-5 py-4 space-y-1">
          {/* Mobile Search */}
          <div className="pb-4 mb-3 border-b border-gray-100">
            <div className="relative">
              <input
                ref={mobileSearchRef}
                type="search"
                placeholder="What are you looking for?"
                className="w-full bg-gray-50 text-gray-900 rounded-full py-2.5 pl-5 pr-12 border border-gray-200 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submitNavbarSearch(e.currentTarget, true);
                  }
                }}
              />
              <button
                type="button"
                aria-label="Search"
                className="absolute right-1 top-1/2 -translate-y-1/2 z-10 bg-primary text-white rounded-full p-2"
                onClick={() => submitNavbarSearch(mobileSearchRef.current, true)}
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden>
                  <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          <Link 
            to="/" 
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium transition-colors ${location.pathname === '/' ? 'bg-primary/5 text-primary' : 'text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setMenuOpen(false)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            Home
          </Link>
          
          <Link 
            to="/products" 
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium transition-colors ${location.pathname === '/products' ? 'bg-primary/5 text-primary' : 'text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setMenuOpen(false)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 01-8 0" />
            </svg>
            Shop All
          </Link>

          {/* Mobile Categories */}
          <div className="py-2">
            <p className="px-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Categories</p>
            <div className="space-y-0.5">
              {navCategories.map(category => {
                const hasSubcategories = category.subcategories && category.subcategories.length > 0;
                const isExpanded = expandedMobileCategory === category.id;
                return (
                  <div key={category.id}>
                    <div className="flex items-center">
                      <button
                        className="flex-1 flex items-center text-left px-3 py-2 text-sm font-medium text-gray-600 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors"
                        onClick={() => {
                          if (hasSubcategories) {
                            setExpandedMobileCategory(isExpanded ? null : category.id);
                          } else {
                            handleCategoryClick(category.id);
                          }
                        }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/30 mr-3 flex-shrink-0"></span>
                        <span className="flex-1">{category.name}</span>
                        {hasSubcategories && (
                          <svg 
                            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            className={`flex-shrink-0 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                          >
                            <path d="M6 9l6 6 6-6"/>
                          </svg>
                        )}
                      </button>
                    </div>
                    {hasSubcategories && (
                      <div className={`ml-7 space-y-0.5 overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-96 opacity-100 mt-0.5' : 'max-h-0 opacity-0'}`}>
                        <button
                          className="block w-full text-left px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/5 rounded-md transition-colors"
                          onClick={() => handleCategoryClick(category.id)}
                        >
                          Shop All {category.name}
                        </button>
                        {category.subcategories.map(subcategory => (
                          <button
                            key={subcategory.id}
                            className="block w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:text-primary rounded-md transition-colors"
                            onClick={() => handleCategoryClick(subcategory.id)}
                          >
                            {subcategory.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-2 mt-1">
          {currentUser ? (
            <div className="space-y-0.5">
              <button
                type="button"
                onClick={() => { setMenuOpen(false); openMiniCart(); }}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium transition-colors text-gray-700 hover:bg-gray-50"
              >
                <div className="relative flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 01-8 0" />
                  </svg>
                  {cartItemCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-accent text-white text-[9px] font-bold rounded-full h-[16px] min-w-[16px] flex items-center justify-center px-0.5">
                      {cartItemCount}
                    </span>
                  )}
                </div>
                Cart
              </button>
              <Link
                to="/orders"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium transition-colors ${location.pathname === '/orders' ? 'bg-primary/5 text-primary' : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setMenuOpen(false)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Orders
              </Link>
              <Link 
                to="/favorites" 
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium transition-colors ${location.pathname === '/favorites' ? 'bg-primary/5 text-primary' : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setMenuOpen(false)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                Favorites
              </Link>
              <Link 
                to="/profile" 
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium transition-colors ${location.pathname === '/profile' ? 'bg-primary/5 text-primary' : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setMenuOpen(false)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />
                </svg>
                Profile
              </Link>
              {isAdmin && (
                <Link 
                  to="/admin" 
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium transition-colors ${location.pathname.startsWith('/admin') ? 'bg-primary/5 text-primary' : 'text-gray-700 hover:bg-gray-50'}`}
                  onClick={() => setMenuOpen(false)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  Admin
                </Link>
              )}
              <button 
                onClick={() => {
                  handleLogout();
                  setMenuOpen(false);
                }} 
                className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-lg text-[15px] font-medium text-red-500 hover:bg-red-50 transition-colors mt-1"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
                </svg>
                Logout
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setMenuOpen(false); openMiniCart(); }}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium transition-colors text-gray-700 hover:bg-gray-50"
              >
                <div className="relative flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 01-8 0" />
                  </svg>
                  {cartItemCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-accent text-white text-[9px] font-bold rounded-full h-[16px] min-w-[16px] flex items-center justify-center px-0.5">
                      {cartItemCount}
                    </span>
                  )}
                </div>
                Cart
              </button>
              <Link
                to="/login"
                className="block text-center w-full py-2.5 border-2 border-primary rounded-full text-sm font-semibold text-primary hover:bg-primary/5 transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                Sign In
              </Link>
              <Link 
                to="/signup" 
                className="block text-center w-full py-2.5 rounded-full text-sm font-semibold text-white bg-primary hover:bg-primary-dark transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                Sign Up
              </Link>
            </div>
          )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
